import {
    Badge,
    Button,
    Card,
    Group,
    Loader,
    Select,
    SimpleGrid,
    Stack,
    Text,
    Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { AI_CONTENT_ACTOR_EMAIL } from '@routellers/types/dist/admin/ai-content-actor';
import {
    AdminCapabilityEnum,
    AdminSectionEnum,
} from '@routellers/types/dist/admin/admin-permissions';
import { IconCheck, IconX } from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { PageHeader } from '../components/page-header';
import { useAuth } from '../providers/auth-context';
import { adminCan } from '../utils/admin-permissions';
import { diffReviewText, type ReviewMark, type ReviewPart } from './qa-diff';

type ReviewItem = {
    id: number;
    adminEmail: string;
    entityType: 'attraction' | 'person' | 'route' | 'quest';
    entityId: number;
    entityTitle: string;
    locale: string | null;
    fieldKey: string;
    oldValue: string | null;
    newValue: string | null;
    status: 'new' | 'ok' | 'bad';
    createdAt: string | Date;
};

const ENTITY_TYPES = ['attraction', 'person', 'route', 'quest'] as const;

export const QaPage = () => {
    const { t } = useTranslation();
    const { admin } = useAuth();
    const canEdit = adminCan(admin, AdminSectionEnum.QA, AdminCapabilityEnum.EDIT);

    const [items, setItems] = useState<ReviewItem[]>([]);
    const [totalNew, setTotalNew] = useState(0);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [adminEmail, setAdminEmail] = useState<string | null>(null);
    const [entityType, setEntityType] = useState<string | null>(null);
    const [adminOptions, setAdminOptions] = useState<Array<{ value: string; label: string }>>([]);

    const loadQuiz = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.contentReviewsApi.quiz({
                adminEmail: adminEmail || undefined,
                entityType: (entityType as ReviewItem['entityType'] | null) || undefined,
                limit: 50,
            });
            setItems(data?.items || []);
            setTotalNew(data?.totalNew ?? 0);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setItems([]);
            setTotalNew(0);
        } finally {
            setLoading(false);
        }
    }, [adminEmail, entityType]);

    useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const { data } = await api.admins.query(undefined);
                if (cancelled) return;
                setAdminOptions(
                    (data || []).map((row) => ({
                        value: row.email,
                        label: `${row.name} (${row.email})`,
                    })),
                );
            } catch {
                if (!cancelled) setAdminOptions([]);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        void loadQuiz();
    }, [loadQuiz]);

    const current = items[0] ?? null;
    const diff = useMemo(
        () => diffReviewText(current?.oldValue || '', current?.newValue || ''),
        [current?.oldValue, current?.newValue],
    );

    const adminFilterOptions = useMemo(
        () => [
            { value: 'all', label: t('pages.qa.filters.allAdmins') },
            { value: AI_CONTENT_ACTOR_EMAIL, label: t('pages.qa.aiEditor') },
            ...adminOptions.filter((option) => option.value !== AI_CONTENT_ACTOR_EMAIL),
        ],
        [adminOptions, t],
    );

    const entityTypeOptions = useMemo(
        () => [
            { value: 'all', label: t('pages.qa.filters.allTypes') },
            ...ENTITY_TYPES.map((type) => ({
                value: type,
                label: t(`pages.qa.entityTypes.${type}`),
            })),
        ],
        [t],
    );

    const mark = async (status: 'ok' | 'bad') => {
        if (!current || !canEdit) return;
        setBusy(true);
        try {
            await api.contentReviewsApi.setStatus({ id: current.id, status });
            setItems((prev) => prev.slice(1));
            setTotalNew((prev) => Math.max(0, prev - 1));
            if (items.length <= 1) {
                await loadQuiz();
            }
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <Stack gap='md'>
            <PageHeader
                title={t('pages.qa.title')}
                action={
                    <Badge size='lg' variant='light' color='brand'>
                        {t('pages.qa.remaining', { count: totalNew })}
                    </Badge>
                }
            />

            <Group gap='md' align='flex-end' wrap='wrap'>
                <Select
                    label={t('pages.qa.filters.admin')}
                    data={adminFilterOptions}
                    value={adminEmail ?? 'all'}
                    onChange={(value) =>
                        setAdminEmail(!value || value === 'all' ? null : value)
                    }
                    searchable
                    clearable
                    w={320}
                />
                <Select
                    label={t('pages.qa.filters.entityType')}
                    data={entityTypeOptions}
                    value={entityType ?? 'all'}
                    onChange={(value) =>
                        setEntityType(!value || value === 'all' ? null : value)
                    }
                    w={220}
                />
            </Group>

            {loading ? (
                <Loader />
            ) : !current ? (
                <Text c='dimmed'>{t('pages.qa.empty')}</Text>
            ) : (
                <Card withBorder padding='lg' radius='md'>
                    <Stack gap='md'>
                        <Group justify='space-between' align='flex-start' wrap='wrap'>
                            <Stack gap={4}>
                                <Title order={4}>{current.entityTitle || `#${current.entityId}`}</Title>
                                <Text size='sm' c='dimmed'>
                                    {t(`pages.qa.entityTypes.${current.entityType}`)} · #
                                    {current.entityId}
                                    {current.locale ? ` · ${current.locale}` : ''}
                                </Text>
                            </Stack>
                            <Stack gap={2} align='flex-end'>
                                <Text size='sm'>
                                    {current.adminEmail === AI_CONTENT_ACTOR_EMAIL
                                        ? t('pages.qa.aiEditor')
                                        : current.adminEmail}
                                </Text>
                                <Text size='xs' c='dimmed'>
                                    {t('pages.qa.field')}: {current.fieldKey}
                                </Text>
                            </Stack>
                        </Group>

                        <Group gap='lg'>
                            <DiffLegend mark='delete' label={t('pages.qa.legendDeleted')} />
                            <DiffLegend mark='insert' label={t('pages.qa.legendAdded')} />
                            <DiffLegend mark='change' label={t('pages.qa.legendChanged')} />
                        </Group>

                        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='md'>
                            <ReviewColumn
                                label={t('pages.qa.was')}
                                parts={current.oldValue?.trim() ? diff.left : null}
                                empty={t('pages.qa.emptyValue')}
                            />
                            <ReviewColumn
                                label={t('pages.qa.became')}
                                parts={current.newValue?.trim() ? diff.right : null}
                                empty={t('pages.qa.emptyValue')}
                            />
                        </SimpleGrid>

                        {canEdit && (
                            <Group justify='flex-end' gap='sm'>
                                <Button
                                    color='red'
                                    variant='light'
                                    leftSection={<IconX size={16} />}
                                    loading={busy}
                                    onClick={() => void mark('bad')}
                                >
                                    {t('pages.qa.bad')}
                                </Button>
                                <Button
                                    color='green'
                                    leftSection={<IconCheck size={16} />}
                                    loading={busy}
                                    onClick={() => void mark('ok')}
                                >
                                    {t('pages.qa.ok')}
                                </Button>
                            </Group>
                        )}
                    </Stack>
                </Card>
            )}
        </Stack>
    );
};

const MARK_STYLE: Record<Exclude<ReviewMark, 'same'>, CSSProperties> = {
    delete: {
        background: 'var(--mantine-color-red-2)',
        color: 'var(--mantine-color-red-9)',
    },
    insert: {
        background: 'var(--mantine-color-green-2)',
        color: 'var(--mantine-color-green-9)',
    },
    change: {
        background: 'var(--mantine-color-yellow-3)',
        color: 'var(--mantine-color-yellow-9)',
    },
};

const markStyle = (mark: Exclude<ReviewMark, 'same'>): CSSProperties => ({
    ...MARK_STYLE[mark],
    borderRadius: 3,
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
});

const DiffLegend = ({ mark, label }: { mark: Exclude<ReviewMark, 'same'>; label: string }) => (
    <Text size='xs' c='dimmed'>
        <span style={markStyle(mark)}>{label}</span>
    </Text>
);

const ReviewParts = ({ parts }: { parts: ReviewPart[] }) => {
    let offset = 0;

    return (
        <>
            {parts.map((part) => {
                const key = `${offset}-${part.mark}`;
                offset += part.text.length;

                if (part.mark === 'same') return <span key={key}>{part.text}</span>;

                return (
                    <span key={key} style={markStyle(part.mark)}>
                        {part.text}
                    </span>
                );
            })}
        </>
    );
};

const ReviewColumn = ({
    label,
    parts,
    empty,
}: {
    label: string;
    parts: ReviewPart[] | null;
    empty: string;
}) => (
    <Stack gap={6}>
        <Text size='xs' tt='uppercase' c='dimmed' fw={600}>
            {label}
        </Text>
        <Text size='sm' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {parts ? <ReviewParts parts={parts} /> : empty}
        </Text>
    </Stack>
);
